import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../lib/utils";

/**
 * Button primitive — Growth Fusion theme.
 *
 * Variants:
 *   - solid (default):  bordered white surface, secondary actions
 *   - primary:          teal-fill — the everyday "go" button (Save, Apply,
 *                       Sign in, etc.). Use freely.
 *   - cta:              TERRACOTTA — reserved for ONE primary call-to-action
 *                       per view (Add customer, Edit customer). Do not use
 *                       more than one cta button on a page. Keeping it rare
 *                       keeps it meaningful.
 *   - ghost:            transparent until hover
 *   - destructive:      red accent, for delete/reject actions
 *   - link:             text-only with underline-on-hover
 *
 * Sizes:
 *   - sm (32px), md (40px, default), lg (48px), icon (40px square)
 *
 * Font weight is medium (500) per the brand spec — never 600/700.
 */
export const buttonVariants = cva(
  // Base
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-[var(--radius-md)] border font-medium",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-transparent",
    "disabled:pointer-events-none disabled:opacity-50",
    "user-select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        solid: [
          "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-strong)]",
          "hover:bg-[var(--color-surface-soft)]",
        ].join(" "),
        primary: [
          "bg-[var(--color-brand)] text-[var(--color-text-on-dark)]",
          "border-[var(--color-brand)]",
          "hover:bg-[var(--color-brand-strong)] hover:border-[var(--color-brand-strong)]",
        ].join(" "),
        cta: [
          "bg-[var(--color-cta)] text-[var(--color-text-on-dark)]",
          "border-[var(--color-cta)]",
          "hover:bg-[var(--color-cta-hover)] hover:border-[var(--color-cta-hover)]",
        ].join(" "),
        ghost: [
          "bg-transparent border-transparent text-[var(--color-text-soft)]",
          "hover:bg-[var(--color-surface-soft)] hover:text-[var(--color-text-strong)]",
        ].join(" "),
        destructive: [
          "bg-[var(--color-status-overdue-text)] text-[var(--color-text-on-dark)]",
          "border-[var(--color-status-overdue-text)]",
          "hover:opacity-90",
        ].join(" "),
        link: [
          "bg-transparent border-transparent text-[var(--color-brand)]",
          "underline-offset-4 hover:underline",
        ].join(" "),
        /* Legacy aliases — kept so old call sites still compile. Resolve
           to the closest new variant. */
        glass: [
          "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-strong)]",
          "hover:bg-[var(--color-surface-soft)]",
        ].join(" "),
        "glass-strong": [
          "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-strong)]",
          "hover:bg-[var(--color-surface-soft)]",
        ].join(" "),
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "solid",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as a different element (Slot pattern for asChild composition). */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
